import { Trees, Waves, Castle, VolumeX, Coffee } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  PROFILE_LABELS, PROFILE_BLURBS, type PleasantnessProfile,
} from "@/utils/profile";

const META: Record<PleasantnessProfile, { Icon: LucideIcon; tint: string; onTint: string }> = {
  nature:   { Icon: Trees,   tint: "bg-emerald-100 text-emerald-700", onTint: "bg-emerald-400/30 text-white" },
  water:    { Icon: Waves,   tint: "bg-sky-100 text-sky-700",         onTint: "bg-sky-300/30 text-white" },
  historic: { Icon: Castle,  tint: "bg-orange-100 text-orange-700",   onTint: "bg-orange-300/30 text-white" },
  quiet:    { Icon: VolumeX, tint: "bg-teal-100 text-teal-700",       onTint: "bg-teal-300/30 text-white" },
  lively:   { Icon: Coffee,  tint: "bg-pink-100 text-pink-700",       onTint: "bg-pink-300/30 text-white" },
};

const ORDER: PleasantnessProfile[] = ["nature", "water", "historic", "quiet", "lively"];

interface Props {
  selected: PleasantnessProfile;
  onChange: (p: PleasantnessProfile) => void;
  /** When true, render compact list rows for the settings sheet. */
  compact?: boolean;
}

export const PleasantnessProfilePicker = ({ selected, onChange, compact }: Props) => {
  return (
    <div className={compact ? "space-y-2" : "grid grid-cols-1 sm:grid-cols-2 gap-2.5"}>
      {ORDER.map((p) => {
        const on = selected === p;
        const m = META[p];
        const Icon = m.Icon;
        return (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-pressed={on}
            className={`text-left px-3 py-3 rounded-2xl border transition-smooth flex items-center gap-3 ${
              on
                ? "bg-pleasant text-primary-foreground border-transparent shadow-glow"
                : "bg-card border-border hover:border-primary/40 hover:shadow-soft"
            }`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-smooth ${
              on ? m.onTint : m.tint
            }`}>
              <Icon className="w-4 h-4" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium leading-tight">{PROFILE_LABELS[p]}</div>
              <div className={`text-xs leading-snug mt-0.5 ${on ? "opacity-90" : "text-muted-foreground"}`}>
                {PROFILE_BLURBS[p]}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};
